import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Skeleton,
  Tooltip,
  LinearProgress,
  Avatar,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Slider,
  SelectChangeEvent,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  CheckCircle as ApproveIcon,
  Token as TokenizeIcon,
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Analytics as AnalyticsIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  TrendingDown as TrendingDownIcon,
  AutoAwesome as AutoAwesomeIcon,
  Architecture as ArchitectureIcon,
  Palette as PaletteIcon,
  Agriculture as AgricultureIcon,
  VerifiedUser as VerifiedUserIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { projectService, Project } from '../services/projectService';
import { kycService } from '../services/kycService';
import { KYCSubmission } from '../types/kyc';
import ProjectDetailsModal from '../components/ProjectDetailsModal';
import KYCStatusBadge from '../components/kyc/KYCStatusBadge';
import KYCReviewDialog from '../components/kyc/KYCReviewDialog';
import { useThemeMode } from '../contexts/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPanel = () => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [tokenizeDialogOpen, setTokenizeDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [autoTokenize, setAutoTokenize] = useState(false);
  const [tokenStandard, setTokenStandard] = useState('standard');
  const [riskThreshold, setRiskThreshold] = useState(50);

  // KYC State
  const [kycSubmissions, setKycSubmissions] = useState<KYCSubmission[]>([]);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycPage, setKycPage] = useState(1);
  const [kycTotal, setKycTotal] = useState(0);
  const [selectedKycSubmission, setSelectedKycSubmission] = useState<KYCSubmission | null>(null);
  const [kycReviewDialogOpen, setKycReviewDialogOpen] = useState(false);
  const [kycSearchTerm, setKycSearchTerm] = useState('');

  useEffect(() => {
    fetchProjects();
    fetchKycSubmissions();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectService.getProjects();
      setAllProjects(response.projects);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchKycSubmissions = async (page: number = 1) => {
    try {
      setKycLoading(true);
      const response = await kycService.getPendingSubmissions(page, 20);
      setKycSubmissions(response.submissions || []);
      setKycTotal(response.total || 0);
      setKycPage(page);
    } catch (error) {
      console.error('Failed to fetch KYC submissions:', error);
    } finally {
      setKycLoading(false);
    }
  };

  const handleKycReview = async (submission: KYCSubmission) => {
    // Fetch full submission details
    try {
      const details = await kycService.getSubmissionDetails(submission.id);
      setSelectedKycSubmission(details);
      setKycReviewDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch KYC details:', error);
      setSelectedKycSubmission(submission);
      setKycReviewDialogOpen(true);
    }
  };

  const handleKycReviewComplete = () => {
    setSuccess('KYC review submitted successfully!');
    fetchKycSubmissions(kycPage);
  };

  const filteredKycSubmissions = kycSubmissions.filter((submission) => {
    if (!kycSearchTerm) return true;
    const search = kycSearchTerm.toLowerCase();
    return (
      submission.full_name?.toLowerCase().includes(search) ||
      submission.wallet_address?.toLowerCase().includes(search) ||
      submission.nationality?.toLowerCase().includes(search)
    );
  });

  const pendingProjects = allProjects.filter(p => p.status === 'submitted' || p.status === 'approved');
  const filteredProjects = pendingProjects.filter(project => {
    const matchesSearch = project.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleApprove = async (project: Project) => {
    setSelectedProject(project);
    setApproveDialogOpen(true);
  };

  const handleTokenize = async (project: Project) => {
    setSelectedProject(project);
    setTokenizeDialogOpen(true);
  };

  const handleViewDetails = (project: Project) => {
    setSelectedProject(project);
    setDetailsDialogOpen(true);
  };

  const confirmApprove = async () => {
    if (!selectedProject) return;

    setActionLoading(true);
    try {
      await projectService.approveProject(selectedProject.id, autoTokenize, tokenStandard);
      setSuccess(`Project "${selectedProject.title || 'Untitled'}" approved successfully!`);
      setApproveDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Approval failed:', error);
      setError('Failed to approve project. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmTokenize = async () => {
    if (!selectedProject) return;

    setActionLoading(true);
    try {
      await projectService.tokenizeProject(selectedProject.id);
      setSuccess(`Project "${selectedProject.title || 'Untitled'}" tokenized successfully!`);
      setTokenizeDialogOpen(false);
      setSelectedProject(null);
      fetchProjects();
    } catch (error) {
      console.error('Tokenization failed:', error);
      setError('Failed to tokenize project. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const getProjectTypeIcon = (type: string) => {
    switch (type) {
      case 'real_estate': return <BusinessIcon />;
      case 'invoice': return <MoneyIcon />;
      case 'art': return <PaletteIcon />;
      case 'equipment': return <ArchitectureIcon />;
      case 'agriculture': return <AgricultureIcon />;
      default: return <BusinessIcon />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'warning';
      case 'approved': return 'info';
      case 'tokenized': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const stats = [
    {
      label: 'Pending Approval',
      value: allProjects.filter(p => p.status === 'submitted').length,
      icon: ScheduleIcon,
      color: 'warning.main',
      gradient: 'linear-gradient(135deg, #FF9800, #FF5722)',
      trend: '+12%',
      trendDirection: 'up',
    },
    {
      label: 'Ready for Tokenization',
      value: allProjects.filter(p => p.status === 'approved').length,
      icon: CheckCircleIcon,
      color: 'info.main',
      gradient: 'linear-gradient(135deg, #2196F3, #00BCD4)',
      trend: '+8%',
      trendDirection: 'up',
    },
    {
      label: 'Total Tokenized',
      value: allProjects.filter(p => p.status === 'tokenized').length,
      icon: TrendingUpIcon,
      color: 'success.main',
      gradient: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
      trend: '+25%',
      trendDirection: 'up',
    },
    {
      label: 'Total Value',
      value: `$${(allProjects.reduce((sum, p) => sum + (p.valuation_usd || 0), 0) / 1000000).toFixed(1)}M`,
      icon: MoneyIcon,
      color: 'primary.main',
      gradient: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
      trend: '+18%',
      trendDirection: 'up',
    },
  ];

  const renderProjectCard = (project: Project, index: number) => (
    <motion.div
      key={project.id}
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        type: "spring",
        stiffness: 100
      }}
      whileHover={{
        y: -8,
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
    >
      <Card
        sx={{
          background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          cursor: 'pointer',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: 'linear-gradient(90deg, #1E40AF, #3B82F6, #0EA5E9)',
            opacity: 0,
            transition: 'opacity 0.3s ease',
          },
          '&:hover::before': {
            opacity: 1,
          },
          '&:hover': {
            boxShadow: isDark
              ? '0 20px 40px rgba(30, 64, 175, 0.3)'
              : '0 20px 40px rgba(30, 64, 175, 0.15)',
            transform: 'translateY(-8px)',
          },
        }}
        className="mouse-track"
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                mr: 2,
                width: 48,
                height: 48,
                background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
              }}
            >
              {getProjectTypeIcon(project.project_type)}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {project.title || 'Untitled Project'}
              </Typography>
              <Chip
                label={project.status}
                color={getStatusColor(project.status) as any}
                size="small"
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 600,
                  textTransform: 'capitalize'
                }}
              />
            </Box>
            <Tooltip title="View Details">
              <IconButton 
                size="small" 
                sx={{ color: 'text.secondary' }}
                onClick={() => handleViewDetails(project)}
              >
                <ViewIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 3, lineHeight: 1.6 }}
          >
            {project.description && project.description.length > 120
              ? `${project.description.substring(0, 120)}...`
              : project.description || 'No description available'}
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Asset Type
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'capitalize' }}>
                  {project.project_type.replace('_', ' ')}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Valuation
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary.main">
                  ${project.valuation_usd?.toLocaleString() || 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {project.risk_score && (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Risk Score
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {project.risk_score.score}/100
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={project.risk_score.score}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: project.risk_score.score > 70 
                      ? 'linear-gradient(90deg, #FF5722, #FF9800)'
                      : project.risk_score.score > 40
                      ? 'linear-gradient(90deg, #FF9800, #FFC107)'
                      : 'linear-gradient(90deg, #4CAF50, #8BC34A)',
                    borderRadius: 3,
                  }
                }}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {project.status === 'submitted' && (
              <Button
                variant="contained"
                color="success"
                startIcon={<ApproveIcon />}
                onClick={() => handleApprove(project)}
                sx={{
                  background: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #388E3C, #689F38)',
                  }
                }}
                size="small"
              >
                Approve
              </Button>
            )}
            {project.status === 'approved' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<TokenizeIcon />}
                onClick={() => handleTokenize(project)}
                sx={{
                  background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                  }
                }}
                size="small"
              >
                Tokenize
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ViewIcon />}
              onClick={() => handleViewDetails(project)}
            >
              Details
            </Button>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        background: isDark
          ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.05) 0%, rgba(14, 165, 233, 0.05) 100%)'
          : 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(14, 165, 233, 0.03) 100%)',
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Header Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <motion.div
                initial={{ rotate: -180, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ duration: 0.8, type: "spring" }}
              >
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                    mr: 3,
                    boxShadow: '0 8px 32px rgba(30, 64, 175, 0.4)',
                  }}
                >
                  <AdminIcon sx={{ fontSize: 32 }} />
                </Avatar>
              </motion.div>
              <Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #3B82F6 0%, #0EA5E9 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  Admin Command Center
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Manage project approvals and tokenization with advanced analytics
                </Typography>
              </Box>
            </Box>
          </Box>

        {error && (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
          >
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </motion.div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
          >
            <Alert 
              severity="success" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          </motion.div>
        )}

        {/* Stats Dashboard */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <Card
                  sx={{
                    background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                    borderRadius: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '3px',
                      background: stat.gradient,
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: stat.color,
                          mr: 2,
                          width: 48,
                          height: 48,
                          background: stat.gradient,
                        }}
                      >
                        <stat.icon />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h4" fontWeight={700} color={stat.color}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.label}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography 
                          variant="caption" 
                          color={stat.trendDirection === 'up' ? 'success.main' : 'error.main'}
                          sx={{ display: 'flex', alignItems: 'center' }}
                        >
                          {stat.trendDirection === 'up' ? <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} /> : <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                          {stat.trend}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Main Content */}
        <Card
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)' }}>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    color: '#3B82F6',
                  },
                },
                '& .MuiTabs-indicator': {
                  background: 'linear-gradient(90deg, #3B82F6, #0EA5E9)',
                },
              }}
            >
              <Tab label="Pending Projects" icon={<ScheduleIcon />} iconPosition="start" />
              <Tab label="KYC Review" icon={<VerifiedUserIcon />} iconPosition="start" />
              <Tab label="Analytics" icon={<AnalyticsIcon />} iconPosition="start" />
              <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {/* Search and Filter Bar */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Search projects..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Filter by Status</InputLabel>
                    <Select
                      value={filterStatus}
                      onChange={(e: SelectChangeEvent<string>) => setFilterStatus(e.target.value)}
                      label="Filter by Status"
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="submitted">Submitted</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="tokenized">Tokenized</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchProjects}
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.5)',
                      color: '#3B82F6',
                      '&:hover': {
                        borderColor: '#3B82F6',
                        background: 'rgba(59, 130, 246, 0.1)',
                      },
                    }}
                  >
                    Refresh
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Projects Grid */}
            <Box sx={{ p: 3, pt: 0 }}>
              {loading ? (
                <Grid container spacing={3}>
                  {[1, 2, 3, 4, 5, 6].map((item) => (
                    <Grid item xs={12} md={6} lg={4} key={item}>
                      <Card>
                        <CardContent>
                          <Skeleton variant="text" width="60%" height={32} />
                          <Skeleton variant="text" width="40%" height={24} />
                          <Skeleton variant="text" width="100%" />
                          <Skeleton variant="text" width="80%" />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : filteredProjects.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <Box sx={{ textAlign: 'center', py: 8 }}>
                    <AutoAwesomeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No projects found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {searchTerm || filterStatus !== 'all' 
                        ? 'Try adjusting your search or filters'
                        : 'All projects have been processed'}
                    </Typography>
                  </Box>
                </motion.div>
              ) : (
                <Grid container spacing={3}>
                  {filteredProjects.map((project, index) => (
                    <Grid item xs={12} md={6} lg={4} key={project.id}>
                      {renderProjectCard(project, index)}
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {/* KYC Review Tab */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    KYC Submissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Review and manage KYC verification requests
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchKycSubmissions(kycPage)}
                  disabled={kycLoading}
                >
                  Refresh
                </Button>
              </Box>

              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search by name, wallet, or nationality..."
                value={kycSearchTerm}
                onChange={(e) => setKycSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{
                  mb: 3,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                  },
                }}
              />

              {/* KYC Submissions List */}
              {kycLoading ? (
                <Grid container spacing={3}>
                  {[1, 2, 3].map((item) => (
                    <Grid item xs={12} md={6} lg={4} key={item}>
                      <Card>
                        <CardContent>
                          <Skeleton variant="text" width="60%" height={32} />
                          <Skeleton variant="text" width="40%" height={24} />
                          <Skeleton variant="text" width="100%" />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : filteredKycSubmissions.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                  <VerifiedUserIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No pending KYC submissions
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {kycSearchTerm ? 'Try adjusting your search' : 'All submissions have been reviewed'}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {filteredKycSubmissions.map((submission, index) => (
                    <Grid item xs={12} md={6} lg={4} key={submission.id}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Card
                          sx={{
                            background: isDark ? 'rgba(17, 24, 39, 0.9)' : 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                            borderRadius: 3,
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              borderColor: '#3B82F6',
                              boxShadow: isDark
                                ? '0 8px 32px rgba(30, 64, 175, 0.3)'
                                : '0 8px 32px rgba(30, 64, 175, 0.15)',
                            },
                          }}
                          onClick={() => handleKycReview(submission)}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                              <Avatar
                                sx={{
                                  bgcolor: 'primary.main',
                                  mr: 2,
                                  width: 48,
                                  height: 48,
                                  background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                                }}
                              >
                                <PersonIcon />
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="h6" fontWeight={600}>
                                  {submission.full_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {submission.nationality}
                                </Typography>
                              </Box>
                              <KYCStatusBadge status={submission.status} />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography variant="caption" color="text.secondary">
                                Wallet Address
                              </Typography>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {submission.wallet_address
                                  ? `${submission.wallet_address.slice(0, 8)}...${submission.wallet_address.slice(-6)}`
                                  : 'N/A'}
                              </Typography>
                            </Box>

                            <Grid container spacing={2}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Documents
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {submission.document_count || submission.documents?.length || 0}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Submitted
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                  {new Date(submission.submitted_at).toLocaleDateString()}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Button
                              variant="outlined"
                              fullWidth
                              startIcon={<ViewIcon />}
                              sx={{ mt: 2 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleKycReview(submission);
                              }}
                            >
                              Review
                            </Button>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              )}

              {/* Pagination */}
              {kycTotal > 20 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 1 }}>
                  <Button
                    variant="outlined"
                    disabled={kycPage === 1 || kycLoading}
                    onClick={() => fetchKycSubmissions(kycPage - 1)}
                  >
                    Previous
                  </Button>
                  <Typography sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                    Page {kycPage} of {Math.ceil(kycTotal / 20)}
                  </Typography>
                  <Button
                    variant="outlined"
                    disabled={kycPage >= Math.ceil(kycTotal / 20) || kycLoading}
                    onClick={() => fetchKycSubmissions(kycPage + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Analytics Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Advanced analytics and insights for project management
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 3,
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Project Type Distribution
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {['real_estate', 'invoice', 'art', 'equipment', 'agriculture'].map((type) => {
                        const count = allProjects.filter(p => p.project_type === type).length;
                        const percentage = allProjects.length > 0 ? (count / allProjects.length) * 100 : 0;
                        return (
                          <Box key={type} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {type.replace('_', ' ')}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {count} ({percentage.toFixed(1)}%)
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  background: 'linear-gradient(90deg, #1E40AF, #3B82F6)',
                                  borderRadius: 4,
                                }
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 3,
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Status Overview
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {['submitted', 'approved', 'tokenized', 'rejected'].map((status) => {
                        const count = allProjects.filter(p => p.status === status).length;
                        const percentage = allProjects.length > 0 ? (count / allProjects.length) * 100 : 0;
                        return (
                          <Box key={status} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {status}
                              </Typography>
                              <Typography variant="body2" fontWeight={600}>
                                {count} ({percentage.toFixed(1)}%)
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                                '& .MuiLinearProgress-bar': {
                                  background: status === 'tokenized'
                                    ? 'linear-gradient(90deg, #4CAF50, #8BC34A)'
                                    : status === 'approved'
                                    ? 'linear-gradient(90deg, #2196F3, #00BCD4)'
                                    : status === 'submitted'
                                    ? 'linear-gradient(90deg, #FF9800, #FF5722)'
                                    : 'linear-gradient(90deg, #F44336, #E91E63)',
                                  borderRadius: 4,
                                }
                              }}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Admin Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure advanced settings for project approval and tokenization
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 3,
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Approval Settings
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={autoTokenize}
                            onChange={(e) => setAutoTokenize(e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Auto-tokenize on approval"
                        sx={{ mb: 2 }}
                      />
                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Token Standard</InputLabel>
                        <Select
                          value={tokenStandard}
                          onChange={(e: SelectChangeEvent<string>) => setTokenStandard(e.target.value)}
                          label="Token Standard"
                        >
                          <MenuItem value="standard">ERC-20 Standard</MenuItem>
                          <MenuItem value="erc3643">ERC-3643 (Security Token)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      p: 3,
                      background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Risk Management
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Risk Score Threshold: {riskThreshold}
                      </Typography>
                      <Slider
                        value={riskThreshold}
                        onChange={(_, value) => setRiskThreshold(value as number)}
                        min={0}
                        max={100}
                        valueLabelDisplay="auto"
                        sx={{
                          mb: 2,
                          '& .MuiSlider-track': {
                            background: 'linear-gradient(90deg, #3B82F6, #0EA5E9)',
                          },
                          '& .MuiSlider-thumb': {
                            background: '#3B82F6',
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Projects with risk scores above this threshold will require manual review
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </Card>

        {/* Approve Dialog */}
        <Dialog
          open={approveDialogOpen}
          onClose={() => setApproveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700
          }}>
            Approve Project
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to approve "{selectedProject?.title || 'Untitled Project'}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will move the project to the approved status and make it ready for tokenization.
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={autoTokenize}
                  onChange={(e) => setAutoTokenize(e.target.checked)}
                  color="primary"
                />
              }
              label="Auto-tokenize after approval"
              sx={{ mb: 2 }}
            />
            
            {autoTokenize && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Token Standard</InputLabel>
                <Select
                  value={tokenStandard}
                  onChange={(e: SelectChangeEvent<string>) => setTokenStandard(e.target.value)}
                  label="Token Standard"
                >
                  <MenuItem value="standard">ERC-20 Standard</MenuItem>
                  <MenuItem value="erc3643">ERC-3643 (Security Token)</MenuItem>
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setApproveDialogOpen(false)} 
              disabled={actionLoading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmApprove}
              variant="contained"
              color="success"
              disabled={actionLoading}
              sx={{
                background: 'linear-gradient(135deg, #4CAF50, #8BC34A)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #388E3C, #689F38)',
                }
              }}
            >
              {actionLoading ? 'Approving...' : 'Approve Project'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tokenize Dialog */}
        <Dialog
          open={tokenizeDialogOpen}
          onClose={() => setTokenizeDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: isDark ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
            }
          }}
        >
          <DialogTitle sx={{ 
            background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 700
          }}>
            Tokenize Project
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to tokenize "{selectedProject?.title || 'Untitled Project'}"?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will create the smart contract and make the tokens available for investment.
            </Typography>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                This action cannot be undone. The project will be permanently tokenized on the blockchain.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={() => setTokenizeDialogOpen(false)} 
              disabled={actionLoading}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmTokenize}
              variant="contained"
              color="primary"
              disabled={actionLoading}
              sx={{
                background: 'linear-gradient(135deg, #1E40AF, #3B82F6)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1E3A8A, #2563EB)',
                }
              }}
            >
              {actionLoading ? 'Tokenizing...' : 'Tokenize Project'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Project Details Modal */}
        <ProjectDetailsModal
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          project={selectedProject}
        />

        {/* KYC Review Dialog */}
        <KYCReviewDialog
          open={kycReviewDialogOpen}
          onClose={() => setKycReviewDialogOpen(false)}
          submission={selectedKycSubmission}
          onReviewComplete={handleKycReviewComplete}
        />
        </motion.div>
      </Container>
    </Box>
  );
};

export default AdminPanel; 